import { colors } from '@src/components/ui/colors';
import { Theme } from './types';

// Define dark theme specific colors
const darkColors = {
    primaryBlack: '#E0E0E0', // Light text color for dark mode
    primaryWhite: '#1F1F1F', // Dark background for dark mode
    primaryBlue: '#389BFF', // Brighter blue for dark mode
    'so-black-0.04': '#2A2A2A',
    'so-black-0.08': '#333333',
    'so-black-0.12': '#3D3D3D',
    'so-black-0.16': '#444444',
    'so-black-0.24': '#555555',
    'so-black-0.32': '#666666',
    'so-black-0.48': '#888888',
    'so-black-0.64': '#999999',
    'so-blue-0.8': '#4CADFF',
    'so-blue-0.9': '#65B7FF',
    'so-blue-0.16': 'rgba(56, 155, 255, 0.16)',
    'so-blue-0.12': 'rgba(56, 155, 255, 0.12)',
    'so-blue-0.08': 'rgba(56, 155, 255, 0.08)',
    'tr-black-0.08': 'rgba(0, 0, 0, 0.08)',
    'tr-blue-0.08': 'rgba(56, 155, 255, 0.08)',
    'tr-blue-0.24': 'rgba(56, 155, 255, 0.24)',
    'tr-blue-0.04': 'rgba(56, 155, 255, 0.04)',
    'tr-green-0.04': 'rgba(75, 181, 67, 0.04)',
    'tr-orange-0.04': 'rgba(255, 149, 0, 0.04)',
    'tr-red-0.04': 'rgba(255, 59, 48, 0.04)',
    white: '#FFFFFF',
};

export const darkTheme: Theme = {
    colors: {
        primary: {
            dark: {
                filled: darkColors['so-blue-0.8'],
                active: darkColors['so-blue-0.9'],
                hover: darkColors['so-blue-0.8'],
                text: darkColors.white,
                border: darkColors['so-blue-0.8'],
                linearHover: darkColors['tr-black-0.08'],
            },
            main: {
                filled: darkColors.primaryBlue,
                active: darkColors['so-blue-0.8'],
                hover: darkColors['so-blue-0.8'],
                text: darkColors.white,
                border: darkColors.primaryBlue,
                linear: darkColors['so-black-0.48'],
                linearHover: darkColors['tr-blue-0.08'],
            },
            light: {
                filled: darkColors['so-blue-0.08'],
                active: darkColors['so-blue-0.16'],
                hover: darkColors['so-blue-0.12'],
                text: darkColors.primaryBlue,
                border: darkColors['so-blue-0.08'],
                linearHover: darkColors['tr-blue-0.24'],
                contentBg: darkColors.primaryBlack,
            },
            linear: {
                filled: darkColors['so-blue-0.8'],
                active: darkColors['so-blue-0.9'],
                hover: darkColors['so-blue-0.8'],
                text: darkColors.white,
                border: darkColors['so-blue-0.8'],
            },
        },
        secondary: {
            dark: {
                filled: darkColors['so-black-0.48'],
                active: darkColors['so-black-0.64'],
                hover: darkColors['so-black-0.48'],
                text: darkColors.white,
                border: darkColors['so-black-0.48'],
            },
            main: {
                filled: darkColors['so-black-0.32'],
                active: darkColors['so-black-0.48'],
                hover: darkColors['so-black-0.32'],
                text: darkColors.primaryBlack,
                border: darkColors['so-black-0.32'],
                icon: darkColors.primaryBlack,
                surfaceBg: darkColors['so-black-0.08'],
                surfaceHover: darkColors['so-black-0.16'],
                contentBg: darkColors['so-black-0.04'],
                divider: darkColors['so-black-0.16'],
            },
            light: {
                filled: darkColors['so-black-0.08'],
                active: darkColors['so-black-0.16'],
                hover: darkColors['so-black-0.12'],
                text: darkColors['so-black-0.48'],
                border: darkColors['so-black-0.08'],
                surfaceBg: darkColors['so-black-0.04'],
                surfaceHover: darkColors['so-black-0.08'],
                contentBg: darkColors.primaryWhite,
                icon: darkColors.primaryBlack,
                divider: darkColors['so-black-0.16'],
            },
            linear: {
                filled: darkColors['so-black-0.48'],
                active: darkColors['so-black-0.64'],
                hover: darkColors['so-black-0.48'],
                text: darkColors.white,
                border: darkColors['so-black-0.48'],
            },
        },
        success: {
            dark: {
                filled: colors['so-green-0.8'],
                active: colors['so-green-0.8'],
                hover: colors['so-green-0.8'],
                text: darkColors.white,
                border: colors['so-green-0.8'],
            },
            main: {
                filled: colors.primaryGreen,
                active: colors['so-green-0.8'],
                hover: colors['so-green-0.8'],
                text: colors.primaryGreen,
                border: colors.primaryGreen,
                icon: colors.primaryGreen,
            },
            light: {
                filled: darkColors['tr-green-0.04'],
                active: colors['so-green-0.16'],
                hover: colors['so-green-0.12'],
                text: colors.primaryGreen,
                border: colors['so-green-0.12'],
                surfaceBg: darkColors['tr-green-0.04'],
                icon: colors.primaryGreen,
            },
            linear: {
                filled: colors['so-green-0.8'],
                active: colors['so-green-0.8'],
                hover: colors['so-green-0.8'],
                text: darkColors.white,
                border: colors['so-green-0.8'],
            },
        },
        warning: {
            dark: {
                filled: colors['so-orange-0.8'],
                active: colors['so-orange-0.8'],
                hover: colors['so-orange-0.8'],
                text: darkColors.white,
                border: colors['so-orange-0.8'],
            },
            main: {
                filled: colors.primaryYellow,
                active: colors['so-orange-0.8'],
                hover: colors['so-orange-0.8'],
                text: colors.primaryYellow,
                border: colors.primaryYellow,
                icon: colors.primaryYellow,
            },
            light: {
                filled: darkColors['tr-orange-0.04'],
                active: colors['so-orange-0.16'],
                hover: colors['so-orange-0.12'],
                text: colors.primaryYellow,
                border: colors['so-orange-0.12'],
                surfaceBg: darkColors['tr-orange-0.04'],
                icon: colors.primaryYellow,
            },
            linear: {
                filled: colors['so-orange-0.8'],
                active: colors['so-orange-0.8'],
                hover: colors['so-orange-0.8'],
                text: darkColors.white,
                border: colors['so-orange-0.8'],
            },
        },
        error: {
            dark: {
                filled: colors['red-0.8'],
                active: colors['red-0.8'],
                hover: colors['red-0.8'],
                text: darkColors.white,
                border: colors['red-0.8'],
            },
            main: {
                filled: colors.primaryRed,
                active: colors['red-0.8'],
                hover: colors['red-0.8'],
                text: colors.primaryRed,
                border: colors.primaryRed,
                icon: colors.primaryRed,
            },
            light: {
                filled: darkColors['tr-red-0.04'],
                active: colors['red-0.16'],
                hover: colors['red-0.12'],
                text: colors.primaryRed,
                border: colors['red-0.12'],
                surfaceBg: darkColors['tr-red-0.04'],
                icon: colors.primaryRed,
            },
            linear: {
                filled: colors['red-0.8'],
                active: colors['red-0.8'],
                hover: colors['red-0.8'],
                text: darkColors.white,
                border: colors['red-0.8'],
            },
        },
        info: {
            dark: {
                filled: darkColors['so-blue-0.8'],
                active: darkColors['so-blue-0.9'],
                hover: darkColors['so-blue-0.8'],
                text: darkColors.white,
                border: darkColors['so-blue-0.8'],
            },
            main: {
                filled: darkColors.primaryBlue,
                active: darkColors['so-blue-0.8'],
                hover: darkColors['so-blue-0.8'],
                text: darkColors.primaryBlue,
                border: darkColors.primaryBlue,
                icon: darkColors.primaryBlue,
            },
            light: {
                filled: darkColors['tr-blue-0.04'],
                active: darkColors['so-blue-0.16'],
                hover: darkColors['so-blue-0.12'],
                text: darkColors.primaryBlue,
                border: darkColors['so-blue-0.12'],
                surfaceBg: darkColors['tr-blue-0.04'],
                icon: darkColors.primaryBlue,
            },
            linear: {
                filled: colors['so-blue-0.8'],
                active: colors['so-blue-0.9'],
                hover: colors['so-blue-0.8'],
                text: darkColors.white,
                border: colors['so-blue-0.8'],
            },
        },
        neutral: {
            dark: {
                filled: darkColors['so-black-0.48'],
                active: darkColors['so-black-0.64'],
                hover: darkColors['so-black-0.48'],
                text: darkColors.white,
                border: darkColors['so-black-0.48'],
                surfaceBg: darkColors['so-black-0.16'],
                surfaceHover: darkColors['so-black-0.24'],
                contentBg: darkColors['so-black-0.08'],
                icon: darkColors.white,
                divider: darkColors['so-black-0.32'],
            },
            main: {
                filled: darkColors['so-black-0.32'],
                active: darkColors['so-black-0.48'],
                hover: darkColors['so-black-0.32'],
                text: darkColors.white,
                border: darkColors['so-black-0.32'],
                surfaceBg: darkColors['so-black-0.08'],
                surfaceHover: darkColors['so-black-0.16'],
                contentBg: darkColors['so-black-0.04'],
                icon: darkColors.primaryBlack,
                divider: darkColors['so-black-0.16'],
            },
            light: {
                filled: darkColors['so-black-0.08'],
                active: darkColors['so-black-0.16'],
                hover: darkColors['so-black-0.08'],
                text: darkColors['so-black-0.48'],
                border: darkColors['so-black-0.08'],
                surfaceBg: darkColors['so-black-0.04'],
                surfaceHover: darkColors['so-black-0.08'],
                contentBg: darkColors.primaryWhite,
                icon: darkColors.primaryBlack,
                divider: darkColors['so-black-0.16'],
            },
            linear: {
                filled: colors['so-black-0.48'],
                active: colors['so-black-0.48'],
                hover: colors['so-black-0.48'],
                text: darkColors.white,
                border: colors['so-black-0.48'],
            },
        },
    },
};
